import { useEffect, useRef, useState, type ReactNode } from 'react';
import BrandMarkEmblem from './BrandMarkEmblem.tsx';
import Sheet from './Sheet.tsx';
import { useMediaQuery, TABLET_QUERY } from './useMediaQuery.ts';

export type NavKey = 'decks' | 'library' | 'guide' | 'workbench';

/**
 * One control, described rather than rendered.
 *
 * DESCRIPTORS, not nodes, and that is the whole design: the same control has
 * to appear as a button in the bar and as a row in the sheet, and handing this
 * component a `<button>` would mean rendering it twice — two DOM nodes, two
 * sets of handlers, two refs, and a menu whose items are subtly not the same
 * items as the toolbar's.
 */
export type HeaderAction = {
  key: string;
  label: string;
  /** `title=` in the bar; the sentence under the label in the sheet. */
  hint?: string;
  onSelect?: () => void;
  /** Renders an anchor instead of a button. */
  href?: string;
  /** New tab + the ↗ affordance. */
  external?: boolean;
  /** `.btn--primary` in the bar. */
  primary?: boolean;
  disabled?: boolean;
  /** Compact label for the bar when the full one is only needed in the sheet
   *  (the settings cog is "⚙" in the bar and "Settings" in the menu). */
  barLabel?: ReactNode;
  /** Replaces the bar's default `.btn` classes. Sign out is the reason it
   *  exists: it has always been a quiet text link rather than a button, and
   *  adopting this component must not restyle the bar it is replacing.
   *  Sheet rows are unaffected — down there everything is a row. */
  barClassName?: string;
  /** Escape hatch for a control that is a component rather than a verb —
   *  ThemeToggle, BuildsIndicator.
   *
   *  MAY RETURN NULL. While ThemeToggle was the only caller a row could assume
   *  its control rendered something; BuildsIndicator renders nothing when no
   *  build is in flight. Both surfaces drop the row entirely in that case —
   *  see BarAction and SheetRow. */
  render?: (ctx: { inSheet: boolean }) => ReactNode;
  /** The renderer supplies the WHOLE sheet row — its own `.app-sheet__row`
   *  button included — and SheetRow skips the label wrapper.
   *
   *  A single toggle wants all 44px as its hit target. Appearance did not have
   *  it: the wrapper was a plain `<div>` with no handler, styled to look
   *  exactly like the `<button>` rows either side of it, so nine tenths of the
   *  row was dead space that invited a tap. A panel control (BuildsIndicator)
   *  wants the opposite — a container it can flow inside, and a button nested
   *  in a button is not that — so it leaves this unset. */
  ownRow?: boolean;
};

export type AppHeaderProps = {
  /** Replaces the "RKH Studio" wordmark. The editor passes the deck's name. */
  title?: string;
  /** `title=` on the wordmark. The editor puts the slug here — that is what
   *  the URL and the filesystem call the deck, so it stays reachable. */
  titleHint?: string;
  /** Drop the emblem. The editor does: it is a workspace, not a front door. */
  hideMark?: boolean;
  /** A quieter second line after the title ("13 slides"). */
  meta?: ReactNode;
  /** Before the brand. The editor's ← back button. */
  lead?: ReactNode;

  showNav?: boolean;
  active?: NavKey | null;
  /**
   * How the host navigates. This component never does it itself — it cannot:
   * the workbench builds from the repo root, which has no react-router, and
   * shared/client may depend on nothing but react.
   *
   * Null or omitted drops the internal nav items entirely, which is exactly
   * what the workbench needs when it is served standalone on :3500 with no
   * studio behind it. That reads as a fact rather than a conditional render:
   * there is nowhere to navigate to.
   */
  onNavigate?: ((href: string) => void) | null;

  /** Always in the bar, never folded (the Dashboard's search). */
  bar?: ReactNode;
  /** Stay in the bar at every width (the editor's Publish). */
  pinned?: HeaderAction[];
  /** Fold into the sheet below TABLET_QUERY. */
  actions?: HeaderAction[];
  /** Tighter geometry, for the editor. */
  compact?: boolean;
};

/**
 * The one app header, rendered by every surface: Decks, Library, Guide, the
 * Workbench and the deck editor.
 *
 * It was four hand-rolled copies of the same `<header className="dashboard__bar">`,
 * which had already drifted — three showed the hummingbird and the workbench
 * showed an orange dot — and the only responsive behaviour any of them had was
 * `flex-wrap: wrap`, so a phone got four stacked rows before any content.
 *
 * Every prop here is a SLOT. There is deliberately no `variant="editor"` and
 * nothing else that names a surface: the five differ only in what the identity
 * block says, whether the nav renders, and which verbs are in the cluster.
 * Everything that is actually hard — the burger, the sheet, the focus trap,
 * the breakpoint decision about where a control lives — is identical, and a
 * surface-named branch is how that stops being true.
 *
 * The markup reuses the existing chrome.css class names on purpose, so
 * adopting it is a no-op on desktop and can be checked by eye.
 */
export default function AppHeader({
  title,
  titleHint,
  hideMark,
  meta,
  lead,
  showNav = true,
  active,
  onNavigate,
  bar,
  pinned,
  actions,
  compact,
}: AppHeaderProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const folded = useMediaQuery(TABLET_QUERY);
  const burgerRef = useRef<HTMLButtonElement>(null);
  // Absence of `title` is the signal that this is the app wordmark rather
  // than something worth keeping — see the note at the brand slot.
  const isWordmark = title === undefined;
  const shownTitle = title ?? 'RKH Studio';

  // A width change can strand the sheet: it is only reachable below the
  // tablet line, so a window widened while it is open would leave a modal
  // layer over a bar that no longer has a button to close it.
  useEffect(() => {
    if (!folded) setSheetOpen(false);
  }, [folded]);

  const navItems = showNav ? NAV.filter((item) => item.external || onNavigate) : [];
  const sheetActions = actions ?? [];
  // The burger earns its place only if the sheet would have something in it.
  // Without this the workbench, served standalone with no nav and no actions,
  // shows a button that opens an empty menu.
  const hasSheet = folded && (navItems.length > 0 || sheetActions.length > 0);

  function go(href: string, external?: boolean) {
    setSheetOpen(false);
    if (external) return; // the anchor's own default does it
    onNavigate?.(href);
  }

  return (
    <>
      <header className={'dashboard__bar' + (compact ? ' dashboard__bar--compact' : '')}>
        {lead}
        <div className="dashboard__brand app-header__brand-slot">
          {!hideMark && <BrandMarkEmblem />}
          {/* The WORDMARK and a real title are the same slot but not the same
              thing, and only the wordmark is expendable: on a phone the
              emblem already says which app this is, and those ~90px are what
              the Dashboard's search needs. A deck's title is the one piece of
              information the editor's bar carries, so it must never be the
              thing that gets dropped to make room. */}
          <span
            className={'brand-name app-header__title' + (isWordmark ? ' app-header__title--wordmark' : '')}
            title={titleHint || undefined}
          >
            {/* The wordmark is a LOCKUP, so its two words are two elements
                rather than a string with a break in it: the line ending is
                part of the mark, not a wrapping accident, and a deck title in
                this same slot must keep wrapping normally. */}
            {isWordmark ? (
              <>
                <span className="app-header__wordmark-line">RKH</span>
                <span className="app-header__wordmark-line">Studio</span>
              </>
            ) : (
              shownTitle
            )}
          </span>
          {meta ? <span className="app-header__meta muted small">{meta}</span> : null}
        </div>

        {/* Folded surfaces are chosen in JS, not hidden in CSS. `folded` is
            read synchronously from matchMedia so there is no flash, and one
            mechanism means the bar and the sheet can never both claim a
            control — an inline `display: contents` wrapper would have beaten
            any CSS rule trying to hide it anyway. */}
        {!folded && navItems.length > 0 && (
          <nav className="global-nav" aria-label="Studio">
            {navItems.map((item) =>
              /* An EXTERNAL item you are already on is not a link. Following
                 it would open a second tab of the page you are reading, which
                 is what the workbench's own hand-rolled strip avoided by
                 rendering the current entry as a plain span. Internal items
                 stay buttons even when active: re-selecting one is harmless
                 and dropping them from the tab order would change the
                 keyboard path on every desktop page. */
              item.external && active === item.key ? (
                <span key={item.key} className="global-nav__link global-nav__link--active">
                  {item.label}
                </span>
              ) : item.external ? (
                <a
                  key={item.key}
                  className="global-nav__link"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={item.hint}
                >
                  {item.label}{' '}
                  <span className="global-nav__ext" aria-hidden="true">
                    ↗
                  </span>
                </a>
              ) : (
                <button
                  key={item.key}
                  type="button"
                  className={'global-nav__link' + (active === item.key ? ' global-nav__link--active' : '')}
                  onClick={() => go(item.href)}
                  title={item.hint}
                >
                  {item.label}
                </button>
              ),
            )}
          </nav>
        )}

        <span className="library__spacer" />

        {bar}

        {/* Folded controls: in the bar above the tablet line, in the sheet
            below it. The same descriptors produce both. */}
        {!folded &&
          sheetActions.map((a) => <BarAction key={a.key} action={a} onNavigated={() => setSheetOpen(false)} />)}

        {pinned?.map((a) => (
          <BarAction key={a.key} action={a} onNavigated={() => setSheetOpen(false)} />
        ))}

        {hasSheet && (
          <button
            ref={burgerRef}
            type="button"
            className="app-header__burger"
            /* The label does NOT flip to "Close": aria-expanded already
               carries the state, and a label that changes under the user
               makes a screen reader announce a different control. */
            aria-label="Menu"
            aria-expanded={sheetOpen}
            aria-controls="app-header-sheet"
            onClick={() => setSheetOpen((v) => !v)}
          >
            <span className="app-header__burger-box" aria-hidden="true">
              <span className="app-header__burger-bar" />
              <span className="app-header__burger-bar" />
              <span className="app-header__burger-bar" />
            </span>
          </button>
        )}
      </header>

      {sheetOpen && hasSheet && (
        <Sheet title="Menu" onClose={() => setSheetOpen(false)}>
          <div id="app-header-sheet">
            {navItems.length > 0 && (
              <div className="app-sheet__group">
                <div className="app-sheet__group-title">Go to</div>
                {navItems.map((item) =>
                  // Same rule as the bar: the external page you are on is
                  // stated, not offered.
                  item.external && active === item.key ? (
                    <div key={item.key} className="app-sheet__row" aria-current="true">
                      <span className="app-sheet__row-label">{item.label}</span>
                    </div>
                  ) : item.external ? (
                    <a
                      key={item.key}
                      className="app-sheet__row"
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setSheetOpen(false)}
                    >
                      <span className="app-sheet__row-label">
                        {item.label}
                        {item.hint ? <span className="app-sheet__row-hint">{item.hint}</span> : null}
                      </span>
                      <span className="app-sheet__row-ext" aria-hidden="true">
                        ↗
                      </span>
                    </a>
                  ) : (
                    <button
                      key={item.key}
                      type="button"
                      className="app-sheet__row"
                      aria-current={active === item.key ? 'true' : undefined}
                      onClick={() => go(item.href)}
                    >
                      <span className="app-sheet__row-label">{item.label}</span>
                    </button>
                  ),
                )}
              </div>
            )}

            {sheetActions.length > 0 && (
              <div className="app-sheet__group">
                <div className="app-sheet__group-title">This page</div>
                {sheetActions.map((a) => (
                  <SheetRow key={a.key} action={a} onDone={() => setSheetOpen(false)} />
                ))}
              </div>
            )}
          </div>
        </Sheet>
      )}
    </>
  );
}

/**
 * The nav roster. Static, because it is the same on every surface and a
 * per-page copy is how Library ended up linking somewhere Guide did not.
 *
 * Workbench is `external` and that is FIXED, not a prop: it is a separate
 * static app served outside the SPA and is meant to be kept open beside the
 * editor, so it always opens in a new tab. A toggle here would be a dial with
 * one legal answer.
 */
const NAV: { key: NavKey; label: string; href: string; hint?: string; external?: boolean }[] = [
  { key: 'decks', label: 'Decks', href: '/' },
  {
    key: 'library',
    label: 'Library',
    href: '/library',
    hint: 'Reusable slides and shared RKH assets: avatars, logos, brand imagery, boilerplate copy',
  },
  {
    key: 'guide',
    label: 'Guide',
    href: '/guide',
    hint: 'How to structure your content and images for the best deck',
  },
  {
    key: 'workbench',
    label: 'Workbench',
    href: '/workbench/',
    hint: 'Browse every primitive and template in the design system (opens in a new tab)',
    external: true,
  },
];

/** One descriptor as it appears in the bar. */
function BarAction({ action, onNavigated }: { action: HeaderAction; onNavigated: () => void }) {
  if (action.render) return <>{action.render({ inSheet: false })}</>;
  const label = action.barLabel ?? action.label;
  const cls =
    action.barClassName ??
    'btn ' + (action.primary ? 'btn--primary' : 'btn--ghost') + (action.primary ? '' : ' btn--sm');
  // A bar label that isn't the real one is an icon or a glyph — the settings
  // cog renders "⚙" — so the accessible name has to come from `label`, or a
  // screen reader announces the character.
  const ariaLabel = action.barLabel != null ? action.label : undefined;
  if (action.href) {
    return (
      <a
        className={cls}
        href={action.href}
        target={action.external ? '_blank' : undefined}
        rel={action.external ? 'noopener noreferrer' : undefined}
        title={action.hint}
        aria-label={ariaLabel}
        onClick={onNavigated}
      >
        {label}
      </a>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      onClick={action.onSelect}
      disabled={action.disabled}
      title={action.hint}
      aria-label={ariaLabel}
    >
      {label}
    </button>
  );
}

/** The same descriptor as a sheet row: full label, hint as its sentence. */
function SheetRow({ action, onDone }: { action: HeaderAction; onDone: () => void }) {
  if (action.render) {
    // A renderer may legitimately render NOTHING — BuildsIndicator returns null
    // when no build is in flight, because an always-present "0 building" chip is
    // noise. On the bar that self-hides correctly; a sheet row wrapped its empty
    // output in a label and left "Builds" sitting there dead on every phone
    // load. The row exists only if its control does.
    const control = action.render({ inSheet: true });
    if (control == null || control === false) return null;
    // The control IS the row. Wrapping it would put the label in a span the
    // control cannot reach and leave the padding unclickable again.
    if (action.ownRow) return <>{control}</>;
    return (
      <div className="app-sheet__row">
        <span className="app-sheet__row-label">
          {action.label}
          {action.hint ? <span className="app-sheet__row-hint">{action.hint}</span> : null}
        </span>
        <span className="app-sheet__row-control">{control}</span>
      </div>
    );
  }
  const body = (
    <>
      <span className="app-sheet__row-label">
        {action.label}
        {action.hint ? <span className="app-sheet__row-hint">{action.hint}</span> : null}
      </span>
      {action.external && (
        <span className="app-sheet__row-ext" aria-hidden="true">
          ↗
        </span>
      )}
    </>
  );
  if (action.href) {
    return (
      <a
        className="app-sheet__row"
        href={action.href}
        target={action.external ? '_blank' : undefined}
        rel={action.external ? 'noopener noreferrer' : undefined}
        onClick={onDone}
      >
        {body}
      </a>
    );
  }
  return (
    <button
      type="button"
      className="app-sheet__row"
      disabled={action.disabled}
      onClick={() => {
        onDone();
        action.onSelect?.();
      }}
    >
      {body}
    </button>
  );
}
